import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Routes as AppRoutes } from "ui/modules/public/navigation";

interface UseApiCallOptions {
  // Redirect kamo kad API vrati 404
  onNotFound?: string;
  // Redirect kamo kad API vrati 403 (default: /unauthorized)
  onForbidden?: string | boolean;
}

interface UseApiCallResult<T> {
  data: T | null;
  isLoading: boolean;
  error: any;
  refetch: () => void;
}

/**
 * Hook za dohvat podataka s API-ja s automatskim handlanjem error statusa.
 *
 * @param apiCall - Funkcija koja vraca Promise (npr. () => getFleetVehicleAPI(id))
 * @param deps - Dependency array — refetch se trigera kad se deps promijene
 * @param options.onNotFound - Ruta na koju redirecta kod 404
 * @param options.onForbidden - Ruta (string) ili true za default /unauthorized kod 403
 *
 * @example
 * const { data, isLoading, refetch } = useApiCall(
 *   () => getFleetVehicleAPI(id!),
 *   [id],
 *   { onNotFound: "/fleet/vehicle/search", onForbidden: true }
 * );
 */
export const useApiCall = <T>(
  apiCall: () => Promise<T>,
  deps: any[],
  options?: UseApiCallOptions
): UseApiCallResult<T> => {
  const navigate = useNavigate();
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await apiCall();
      setData(result);
    } catch (err: any) {
      setError(err);
      const status = err?.response?.status;

      if (status === 404 && options?.onNotFound) {
        navigate(options.onNotFound, { replace: true });
      }

      if (status === 403 && options?.onForbidden !== undefined) {
        const route = typeof options.onForbidden === "string"
          ? options.onForbidden
          : AppRoutes.UnauthorizedPage;
        navigate(route, { replace: true });
      }
    } finally {
      setIsLoading(false);
    }
  }, deps);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, isLoading, error, refetch: fetch };
};
